import { type FC, type FormEvent, type ReactNode, useCallback, useEffect, useRef } from 'react'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { type AddSummary, hasFixableTerms, parseTermInput } from './vocabularyUtils'

const MIN_ROWS = 3
const MAX_ROWS = 10

const rowsForValue = (value: string) => Math.min(Math.max(value.split('\n').length, MIN_ROWS), MAX_ROWS)

interface ResultLineProps {
  icon: string
  muted?: boolean
  bold?: boolean
  children: ReactNode
}

const ResultLine: FC<ResultLineProps> = ({ icon, muted = false, bold = false, children }) => (
  <div className={classNames('d-flex align-items-start zocks-gap-0.5', muted ? 'zocks-text-gray-600' : 'zocks-text-gray-900')}>
    <i className={classNames('bi', icon, 'fs-6 flex-shrink-0')} aria-hidden='true' />
    <span className={classNames({ 'fw-bold': bold })}>{children}</span>
  </div>
)

interface VocabularyAddFormProps {
  value: string
  submittedValue: string | null
  summary: AddSummary | null
  isSubmitting: boolean
  remaining: number
  termLimit: number
  maxWordsPerTerm: number
  maxTermLength: number
  hideCancel?: boolean
  prompt?: string
  onChange: (value: string) => void
  onSubmit: () => void
  onClose: () => void
}

export const VocabularyAddForm: FC<VocabularyAddFormProps> = ({
  value,
  submittedValue,
  summary,
  isSubmitting,
  remaining,
  termLimit,
  maxWordsPerTerm,
  maxTermLength,
  hideCancel = false,
  prompt,
  onChange,
  onSubmit,
  onClose,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const cardRef = useRef<HTMLFormElement>(null)

  const scrollCardIntoView = useCallback(() => {
    const card = cardRef.current
    if (!card) {
      return
    }
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    card.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' })
  }, [])

  useEffect(() => {
    scrollCardIntoView()
    const textarea = textareaRef.current
    if (!textarea) {
      return
    }
    textarea.focus()
    const caret = textarea.value.length
    textarea.setSelectionRange(caret, caret)
  }, [scrollCardIntoView])

  useEffect(() => {
    if (!summary) {
      return
    }
    if (textareaRef.current) {
      textareaRef.current.scrollTop = 0
    }
    const card = cardRef.current
    if (!card) {
      return
    }
    const { top } = card.getBoundingClientRect()
    if (top >= 0 && top <= window.innerHeight) {
      return
    }
    scrollCardIntoView()
  }, [summary, scrollCardIntoView])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit()
  }

  const isAtLimit = remaining === 0
  const duplicateCount = summary?.duplicate.length ?? 0
  const duplicateOutcome =
    duplicateCount === 1
      ? `"${summary?.duplicate[0]}" is already in your vocabulary or used by Zocks`
      : `${duplicateCount} skipped — already in your vocabulary or used by Zocks`
  const hasResolvedOutcomes = !!summary?.added || duplicateCount > 0
  const hasFixableOutcomes = !!summary && hasFixableTerms(summary)
  const parsedCount = parseTermInput(value).length
  const isOverCapacity = parsedCount > remaining
  const isUnchangedSinceSubmit = submittedValue !== null && value === submittedValue
  const canSubmit = value.trim().length > 0 && !isAtLimit && !isUnchangedSinceSubmit

  return (
    <Form
      noValidate
      ref={cardRef}
      aria-label='Add vocabulary terms'
      onSubmit={handleSubmit}
      className='zocks-scroll-mt-1 d-flex flex-column zocks-gap-0.5 zocks-px-0.75 py-3 border zocks-border-color-gray-400 rounded-3 zocks-bg-color-gray-100'
    >
      {prompt ? <div className='paragraph-medium zocks-text-gray-900 mb-2'>{prompt}</div> : null}
      {summary ? (
        <div className='paragraph-medium d-flex flex-column zocks-gap-0.25' role='status'>
          {summary.added > 0 ? (
            <ResultLine icon='bi-check-lg'>{summary.added === 1 ? '1 added to custom vocabulary' : `${summary.added} added to custom vocabulary`}</ResultLine>
          ) : null}
          {duplicateCount > 0 ? <ResultLine icon='bi-files'>{duplicateOutcome}</ResultLine> : null}
          <div className={classNames('d-flex flex-column zocks-gap-0.25', { 'mt-3': hasResolvedOutcomes && hasFixableOutcomes })}>
            {summary.tooManyWords.length > 0 ? (
              <ResultLine icon='bi-pencil' bold>
                {summary.tooManyWords.length === 1
                  ? `"${summary.tooManyWords[0]}" is over ${maxWordsPerTerm} words. Shorten it and try again.`
                  : `${summary.tooManyWords.length} terms are over ${maxWordsPerTerm} words. Shorten them and try again.`}
              </ResultLine>
            ) : null}
            {summary.termTooLong.length > 0 ? (
              <ResultLine icon='bi-pencil' bold>
                {summary.termTooLong.length === 1
                  ? `"${summary.termTooLong[0]}" is over ${maxTermLength} characters. Shorten it and try again.`
                  : `${summary.termTooLong.length} terms are over ${maxTermLength} characters. Shorten them and try again.`}
              </ResultLine>
            ) : null}
            {summary.overLimit.length > 0 ? (
              <ResultLine icon='bi-exclamation-lg' bold>
                {summary.overLimit.length === 1
                  ? `"${summary.overLimit[0]}" is over the ${termLimit}-term limit. Remove a term below to make room.`
                  : `${summary.overLimit.length} terms are over the ${termLimit}-term limit. Remove terms below to make room.`}
              </ResultLine>
            ) : null}
          </div>
        </div>
      ) : null}
      {!summary && isAtLimit ? (
        <div className='paragraph-medium' role='status'>
          <ResultLine icon='bi-exclamation-lg'>You've reached the {termLimit}-term limit</ResultLine>
          <div className='mt-3'>
            <ResultLine icon='bi-dash-lg' muted>
              Remove terms below to add new ones.
            </ResultLine>
          </div>
        </div>
      ) : null}
      <Form.Control
        ref={textareaRef}
        as='textarea'
        rows={rowsForValue(value)}
        spellCheck={false}
        aria-label='Terms'
        disabled={isAtLimit || isSubmitting}
        value={value}
        className='paragraph-medium'
        onChange={(event) => onChange(event.currentTarget.value)}
      />
      <div className='paragraph-small zocks-text-gray-600'>
        <div className='d-flex align-items-start justify-content-between gap-3'>
          <span>Add one term per line, up to {maxWordsPerTerm} words each.</span>
          {parsedCount > 0 ? (
            <span className={classNames('text-nowrap flex-shrink-0', { 'zocks-text-danger-200': isOverCapacity })}>
              {parsedCount} {parsedCount === 1 ? 'term' : 'terms'}
              {isOverCapacity ? ` · ${parsedCount - remaining} over the limit` : ''}
            </span>
          ) : null}
        </div>
        <div>Use the exact spelling and capitalization you want. Add "eMoney" rather than "emoney". Each term can be added once.</div>
      </div>
      <div className='d-flex align-items-center gap-3'>
        <Button type='submit' variant='primary' loading={isSubmitting} disabled={!canSubmit}>
          Add terms
        </Button>
        {hideCancel ? null : (
          <Button type='button' variant='link' className='text-decoration-none' disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
        )}
      </div>
    </Form>
  )
}
